import { geoTagsIds } from '@/data/filterData';
import USStatesList from '@/data/USStatesList';
import { cloneDeep, without } from 'lodash';
import { comparisonTypes } from './constants';

/**
 * Given a list of tag filters it converst the tag criterias of them 
 * @param {*} filters 
 * @returns 
 */
export function convertComparisonTypes(filters) {
  if (!filters || !filters.length) {
    return filters;
  }
  for (let i = 0; i < filters.length; i++) {

    if (!filters[i].tagCriteria || !filters[i].tagCriteria.length) {
      continue;
    }

    for (let j = 0; j < filters[i].tagCriteria.length; j++) {
      filters[i].tagCriteria[j] = convertComparisonType(filters[i].tagCriteria[j]);
    }
  }
  return filters;
}

/**
 * Given a Filter Criteria it Adds data to it to convert it easier into a backend filter criteria
 * @param {*} filter 
 * @returns 
 */
export function convertComparisonType(filter) {
  filter.isGeoTag = !!geoTagsIds.find((tag) => {
    return tag.tagId === filter.tagId;
  });
  filter.isNumber = Boolean(filter.useNumericalValue) || Boolean(filter.comparisonTypeTemp?.useNumericalValue);

  filter.comparisonTypeTemp = comparisonTypes.find((kv) => {
    return kv.key === filter.comparisonType
      && filter.isNegativeMatch === kv.isNegativeMatch
      && kv.forBulkTags === !!filter.forBulkTags
      && kv.forMultipleValues === !!filter.forMultipleValues
      && kv.forGeoTags === Boolean(filter.isGeoTag);
  });

  if (!filter.comparisonTypeTemp) {
    filter.comparisonTypeTemp = comparisonTypes.find(c => c.defaultComparisonType);
  }
  return filter;
}

/**
 * Transform a geo tag, ie US State, to have state name as a value and condense the criteria into one object
 * @param {*} criteria 
 * @returns 
 */
export function transformGeoTag(criteria) {
  criteria.forEach((c) => {
    // Set the index of the geo filter if exists so it can be inserted back in at that index
    const geoIndex = c.tagCriteria?.findIndex((tc) => tc.isGeoTag === true);
    const geoCriteria = c.tagCriteria?.filter((tc) => tc.isGeoTag === true);
    const notGeoCriteria = c.tagCriteria?.filter((tc) => tc.isGeoTag != true);
    if (geoCriteria && geoCriteria.length) {
      geoCriteria.forEach((gc) => {
        if (gc.comparisonType == 'IN' && !Array.isArray(gc.value) && gc.value !== '') {
          const stateArray = gc.value.split(',');
          if (stateArray.length > 0) {
            gc.value = changeStateValueToName(stateArray.pop());
            stateArray.forEach((stateValue) => {
              geoCriteria.push({
                ...gc,
                value: changeStateValueToName(stateValue)
              });
            });
          }

        } else {
          if (typeof gc.value == 'string') {
            gc.value = changeStateValueToName(gc.value);
          }
        }

      });
      // Insert geoCriteria back into array
      notGeoCriteria.splice(geoIndex, 0, transformGeoBulkCriteria(geoCriteria));
      c.tagCriteria = notGeoCriteria.flat();
    }
  });
  return criteria;
}

/**
 * Transforms a UI Geo Tag Criteria into a backend compatible object
 * @param {*} criteria 
 * @returns 
 */
export function transformGeoTagBackendObj(criteria) {
  criteria.forEach((c) => {
    const tempCriteria = [];
    c.tagCriteria.forEach((tagCriteria) => {
      if (!tagCriteria.isGeoTag) {
        tempCriteria.push(tagCriteria);
      } else {
        if (tagCriteria.comparisonType == 'IN') {
          const transformedStates = tagCriteria.value.map((state) => changeStateValueToAbbr(state));
          tempCriteria.push({
            ...tagCriteria,
            value: transformedStates.join(',')
          });
        } else {
          tagCriteria.value.forEach((value) => {
            tempCriteria.push({
              ...tagCriteria,
              value: changeStateValueToAbbr(value)
            });
          });

          if (!tagCriteria.value.length) {
            tempCriteria.push({
              ...tagCriteria,
              value: ''
            });
          }
        }
      }
    });

    if (tempCriteria && tempCriteria.length > 0) {
      c.tagCriteria = tempCriteria;
    }
  });
  return criteria;
}

/**
 * Supporting function for geo transformation, flattens the criteria object with an array of state values
 * @param {*} criteria 
 * @returns 
 */
export function transformGeoBulkCriteria(criteria) {
  let negativeMatchGeo = {};
  let matchedGeo = {};
  const finalCriterias = [];

  // reduces geo criteria for isNegativeMatch and isMatch to different criterias

  criteria.forEach((item) => {
    if (item.comparisonTypeTemp.isNegativeMatch) {
      const index = finalCriterias.findIndex(i => i.comparisonTypeTemp?.isNegativeMatch);

      negativeMatchGeo = {
        ...item,
        value: without([negativeMatchGeo.value].concat(item.value).flat().sort((a, b) => a.localeCompare(b)), undefined)
      };


      if (index > -1) {
        finalCriterias[index] = negativeMatchGeo;
      } else {
        finalCriterias.push(negativeMatchGeo);
      }
    } else {
      const index = finalCriterias.findIndex(i => !i.comparisonTypeTemp?.isNegativeMatch);

      matchedGeo = {
        ...item,
        value: without([matchedGeo.value].concat(item.value).flat().sort((a, b) => a.localeCompare(b)), undefined)
      };

      if (index > -1) {
        finalCriterias[index] = matchedGeo;
      } else {
        finalCriterias.push(matchedGeo);
      }
    }
  });

  return finalCriterias;
}

/**
 * Receives a state value (abreviated) and returns the full state name
 * @param {*} value Abreviated name of the state
 * @returns full state name
 */
export function changeStateValueToName(value) {
  if (value) {
    const state = USStatesList.find((s) => s.abbreviation === value);
    return state.name;
  }
}

/**
 * Receives a full state name and returns the abreviation
 * @param {*} value Full state name
 * @returns abreviated state name
 */
export function changeStateValueToAbbr(value) {
  const state = USStatesList.find((s) => s.name === value);
  return state ? state.abbreviation : '';
}

/**
 * Given a Call flow Scene link it parses it's filters into backend conmpatible criteria
 * @param {*} link Link from the call flow scene
 * @returns Array of Criterias of the link
 */
export function getCriteria(link) {
  const clonedCriteria = [];

  for (const criteria of link.criteria) {
    if (criteria.tagCriteria) {
      const parentTagCriteria = {
        tagCriteria: []
      };

      for (let i = 0; i < criteria.tagCriteria.length; i++) {
        if (criteria.tagCriteria[i].forBulkTags) {
          parentTagCriteria.bulkCriteria = {
            id: criteria.tagCriteria[i].value,
            tagId: criteria.tagCriteria[i].tagId,
            isNegativeMatch: criteria.tagCriteria[i].isNegativeMatch
          };

          continue;
        }

        const tagCriteria = cloneDeep(criteria.tagCriteria[i]);

        if (criteria.tagCriteria[i].tagId) {
          tagCriteria.tagId = tagCriteria.tagId.replace('[nodeId]', link.originNode.id);
        }

        if (criteria.tagCriteria[i].tagIds) {
          tagCriteria.tagIds = replaceNodeIdTokensInTagId(tagCriteria.tagIds, link.originNode.id, '[nodeId]');
        }

        parentTagCriteria.tagCriteria.push(tagCriteria);
      }

      clonedCriteria.push(parentTagCriteria);
    }
  }
  return clonedCriteria;
}

/**
 * For a list of tagIds, if the tag contains the token passed it will change it with the parent Node id
 * @param {*} tagIds list of tags to check and replace
 * @param {*} parentNodeId value to replace the token with
 * @param {*} token value to be replace with the parentNodeId
 * @returns List of tagIds where the token has been swapped with the parentNodeId
 */
export function replaceNodeIdTokensInTagId(tagIds, parentNodeId, token) {
  const replacedTagIds = [];

  for (let i = 0; i < tagIds.length; i++) {
    replacedTagIds[i] = tagIds[i].replace(token, parentNodeId);
  }

  return replacedTagIds;
}
